import React, { useState, useEffect, useCallback } from 'react';
import salesRadarService from '../../services/salesRadarService';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Color scale for heatmap
const getHeatmapColor = (value, maxValue) => {
  const normalizedValue = maxValue > 0 ? value / maxValue : 0;
  
  if (normalizedValue >= 0.8) return '#1a237e'; // Deep blue - very high
  if (normalizedValue >= 0.6) return '#303f9f'; // Blue - high
  if (normalizedValue >= 0.4) return '#5c6bc0'; // Light blue - medium-high
  if (normalizedValue >= 0.2) return '#9fa8da'; // Very light blue - medium
  if (normalizedValue > 0) return '#c5cae9'; // Pale blue - low
  return '#f5f5f5'; // Gray - no activity
};

const getContrastTextColor = (value, maxValue) => {
  const normalizedValue = maxValue > 0 ? value / maxValue : 0;
  return normalizedValue >= 0.4 ? '#ffffff' : '#333333';
};

const formatHour = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

// Heatmap cell component
const HeatmapCell = ({ value, maxValue, day, hour, onClick }) => {
  return (
    <div
      onClick={() => onClick && onClick(day, hour)}
      title={`${DAYS_OF_WEEK[day]} ${formatHour(hour)}: ${value} activities`}
      style={{
        width: '100%',
        height: '32px',
        backgroundColor: getHeatmapColor(value, maxValue),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        fontSize: '10px',
        fontWeight: 'bold',
        color: getContrastTextColor(value, maxValue)
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.zIndex = '1';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.zIndex = '0';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {value > 0 && value}
    </div>
  );
};

// Drilldown Modal
const DrilldownModal = ({ isOpen, day, hour, data, loading, onClose }) => {
  if (!isOpen) return null;

  const tableHeaderStyle = {
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--gray-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid var(--gray-200)'
  };

  const tableCellStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid var(--gray-100)'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ 
        width: '100%', 
        maxWidth: '800px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        margin: '1rem'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>
            Activity Details - {DAYS_OF_WEEK[day]} at {formatHour(hour)}
          </h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <span style={{ fontSize: '1.5rem' }}>⏳</span>
              <span style={{ marginLeft: '0.5rem', color: 'var(--gray-600)' }}>Loading...</span>
            </div>
          ) : data?.activities?.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: 'var(--gray-50)' }}>
                    <tr>
                      <th style={tableHeaderStyle}>Time</th>
                      <th style={tableHeaderStyle}>Lead</th>
                      <th style={tableHeaderStyle}>Activity Type</th>
                      <th style={tableHeaderStyle}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.activities.map((activity, index) => (
                      <tr key={index}>
                        <td style={tableCellStyle}>
                          {new Date(activity.timestamp).toLocaleString()}
                        </td>
                        <td style={tableCellStyle}>
                          {activity.lead_name || activity.lead_email}
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            border: '1px solid var(--gray-300)',
                            borderRadius: '0.25rem'
                          }}>
                            {activity.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          {activity.details || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.summary && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--gray-200)' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem' }}>Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>Total Activities</p>
                      <p style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0.25rem 0 0 0' }}>{data.summary.total}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>Unique Leads</p>
                      <p style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0.25rem 0 0 0' }}>{data.summary.unique_leads}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>Most Common</p>
                      <p style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0.25rem 0 0 0' }}>{data.summary.most_common_type || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--blue-50)',
              border: '1px solid var(--blue-200)',
              borderRadius: '0.5rem',
              color: 'var(--blue-700)'
            }}>
              ℹ️ No activities found for this time slot.
            </div>
          )}
        </div>
        <div style={{ 
          padding: '1rem 1.5rem', 
          borderTop: '1px solid var(--gray-200)', 
          display: 'flex', 
          justifyContent: 'flex-end'
        }}>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const ActivityHeatmap = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState(null);
  const [viewMode, setViewMode] = useState('aggregate'); // 'aggregate' or 'individual'
  const [selectedLead, setSelectedLead] = useState(null);
  const [leads, setLeads] = useState([]);
  const [timezone, setTimezone] = useState('UTC');
  const [error, setError] = useState(null);
  const [drilldownDialog, setDrilldownDialog] = useState({ open: false, day: null, hour: null });
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [dateRange, setDateRange] = useState(30);
  const [activityType, setActivityType] = useState('all');

  const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Central European (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan (JST)' }
  ];

  const activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'page_view', label: 'Page Views' },
    { value: 'email_open', label: 'Email Opens' },
    { value: 'email_click', label: 'Email Clicks' },
    { value: 'form_submit', label: 'Form Submissions' },
    { value: 'download', label: 'Downloads' }
  ];

  const selectStyle = {
    padding: '0.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    backgroundColor: 'white',
    minWidth: '150px'
  };

  const loadHeatmapData = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await salesRadarService.getActivityHeatmap(projectId, {
        date_range: dateRange,
        activity_type: activityType !== 'all' ? activityType : undefined,
        lead_id: viewMode === 'individual' && selectedLead ? selectedLead : undefined,
        timezone
      });

      if (response.success) {
        setHeatmapData(response.data);
      }
    } catch (err) {
      setError('Failed to load activity heatmap');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId, dateRange, activityType, viewMode, selectedLead, timezone]);

  const loadLeads = useCallback(async () => {
    if (!projectId) return;

    try {
      const response = await salesRadarService.getLeads({ project_id: projectId, limit: 50 });
      if (response.success) {
        setLeads(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load leads:', err);
    }
  }, [projectId]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    loadHeatmapData();
  }, [loadHeatmapData]);

  const handleCellClick = async (day, hour) => {
    setDrilldownDialog({ open: true, day, hour });
    setDrilldownData(null);
    setDrilldownLoading(true);

    try {
      const response = await salesRadarService.getActivityDrilldown(projectId, {
        day_of_week: day,
        hour,
        date_range: dateRange,
        activity_type: activityType !== 'all' ? activityType : undefined,
        lead_id: viewMode === 'individual' && selectedLead ? selectedLead : undefined
      });

      if (response.success) {
        setDrilldownData(response.data);
      }
    } catch (err) {
      console.error('Failed to load drilldown data:', err);
    } finally {
      setDrilldownLoading(false);
    }
  };

  const getMaxValue = () => {
    if (!heatmapData?.matrix) return 1;
    return Math.max(...heatmapData.matrix.flat(), 1);
  };

  if (loading && !heatmapData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ color: 'var(--gray-600)' }}>Loading heatmap...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '1rem',
        backgroundColor: 'var(--red-50)',
        border: '1px solid var(--red-200)',
        borderRadius: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span style={{ color: 'var(--red-700)' }}>❌ {error}</span>
        <button className="btn" onClick={loadHeatmapData}>Retry</button>
      </div>
    );
  }

  const maxValue = getMaxValue();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>🔥</span>
          Activity Heatmap
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: '0.375rem', overflow: 'hidden', border: '1px solid var(--gray-300)' }}>
            <button
              onClick={() => setViewMode('aggregate')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                backgroundColor: viewMode === 'aggregate' ? 'var(--primary-600)' : 'white',
                color: viewMode === 'aggregate' ? 'white' : 'var(--gray-700)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontSize: '0.875rem'
              }}
            >
              👥 Aggregate
            </button>
            <button
              onClick={() => setViewMode('individual')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderLeft: '1px solid var(--gray-300)',
                backgroundColor: viewMode === 'individual' ? 'var(--primary-600)' : 'white',
                color: viewMode === 'individual' ? 'white' : 'var(--gray-700)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontSize: '0.875rem'
              }}
            >
              👤 Individual
            </button>
          </div>
          <button
            onClick={loadHeatmapData}
            style={{
              padding: '0.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '1.25rem',
              borderRadius: '0.25rem'
            }}
            title="Refresh"
          >
            🔄
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '1rem 1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem' }}>
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(Number(e.target.value))}
              style={selectStyle}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem' }}>
              Activity Type
            </label>
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              style={selectStyle}
            >
              {activityTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem' }}>
              🌍 Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              style={selectStyle}
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          {viewMode === 'individual' && (
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                Select Lead
              </label>
              <select
                value={selectedLead || ''}
                onChange={(e) => setSelectedLead(e.target.value || null)}
                style={selectStyle}
              >
                <option value="">All Leads</option>
                {leads.map(lead => (
                  <option key={lead.id} value={lead.id}>
                    {lead.first_name} {lead.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '1.5rem' }}>
        {/* Heatmap */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>
              Activity by Day & Hour
            </h2>
            <span title="Click on any cell to see activity details" style={{ cursor: 'help', fontSize: '1.25rem' }}>
              🔍
            </span>
          </div>

          {heatmapData && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: '700px' }}>
                {/* Hour labels */}
                <div style={{ display: 'flex', marginLeft: '80px' }}>
                  {HOURS.filter(h => h % 2 === 0).map(hour => (
                    <div key={hour} style={{ width: `${100/12}%`, textAlign: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        {formatHour(hour)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Heatmap grid */}
                {DAYS_OF_WEEK.map((day, dayIndex) => (
                  <div key={day} style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                    <span style={{ 
                      width: '80px', 
                      textAlign: 'right', 
                      paddingRight: '0.75rem', 
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    }}>
                      {day}
                    </span>
                    <div style={{ display: 'flex', flex: 1 }}>
                      {HOURS.map(hour => (
                        <div key={hour} style={{ flex: 1, padding: '0 1px' }}>
                          <HeatmapCell
                            value={heatmapData.matrix?.[dayIndex]?.[hour] || 0}
                            maxValue={maxValue}
                            day={dayIndex}
                            hour={hour}
                            onClick={handleCellClick}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Color legend */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Low</span>
                  {[0, 0.2, 0.4, 0.6, 0.8, 1].map((val, idx) => (
                    <div 
                      key={idx}
                      style={{ 
                        width: '30px', 
                        height: '16px', 
                        backgroundColor: getHeatmapColor(val * maxValue, maxValue),
                        borderRadius: '2px'
                      }} 
                    />
                  ))}
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>High</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="card" style={{ padding: '1.5rem' }}>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', margin: 0 }}>Total Activities</p>
            <p style={{ fontSize: '2rem', fontWeight: 600, margin: '0.5rem 0 0 0' }}>
              {heatmapData?.total_activities?.toLocaleString() || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>
              in last {dateRange} days
            </p>
          </div>
          
          <div className="card" style={{ padding: '1.5rem', backgroundColor: 'var(--green-50)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📈</span>
              <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Peak Activity</span>
            </div>
            <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0.5rem 0 0 0' }}>
              {heatmapData?.peak_day || 'N/A'}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: 0 }}>
              at {heatmapData?.peak_hour !== undefined ? formatHour(heatmapData.peak_hour) : 'N/A'}
            </p>
          </div>

          <div className="card" style={{ padding: '1.5rem', backgroundColor: 'var(--yellow-50)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📉</span>
              <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Lowest Activity</span>
            </div>
            <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0.5rem 0 0 0' }}>
              {heatmapData?.low_day || 'N/A'}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: 0 }}>
              at {heatmapData?.low_hour !== undefined ? formatHour(heatmapData.low_hour) : 'N/A'}
            </p>
          </div>

          <div className="card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span>⏰</span>
              <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Active Hours</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {heatmapData?.most_active_hours?.slice(0, 5).map(hour => (
                <span
                  key={hour}
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    border: '1px solid var(--primary-300)',
                    borderRadius: '9999px',
                    color: 'var(--primary-700)'
                  }}
                >
                  {formatHour(hour)}
                </span>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: '1.5rem' }}>
            <h4 style={{ fontWeight: 500, fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>
              Activity Breakdown
            </h4>
            {heatmapData?.activity_breakdown && (
              <div>
                {Object.entries(heatmapData.activity_breakdown).slice(0, 5).map(([type, count]) => (
                  <div 
                    key={type}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                      {type.replace(/_/g, ' ')}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drilldown Modal */}
      <DrilldownModal
        isOpen={drilldownDialog.open}
        day={drilldownDialog.day}
        hour={drilldownDialog.hour}
        data={drilldownData}
        loading={drilldownLoading}
        onClose={() => setDrilldownDialog({ open: false, day: null, hour: null })}
      />
    </div>
  );
};

export default ActivityHeatmap;
